import shouldNotAnimate from "helpers/shouldNotAnimate";

/**
 * Returns a promise that resolves when scrolling to an element is complete.
 * Note that the helper will also try to focus the element once the scroll
 * completes, so be sure the element is either inherently focusable or has
 * the proper tabindex.
 *
 * @param element The element to scroll to
 * @param options The scrollIntoView options, overrides the scroll behavior to 'smooth'
 */
export default function smoothScroll(
  element: HTMLElement,
  options?: ScrollIntoViewOptions,
): Promise<void> {
  const disableAnimations = shouldNotAnimate();

  // If the user has the prefers reduced motion accessibility setting turned on,
  // skip the animation and jump straight to the section.
  if (disableAnimations) {
    element.focus();
    return Promise.resolve();
  } else {
    // If you don't wrap scrollIntoView in a setTimeout it won't scroll
    setTimeout(() => {
      element.scrollIntoView({ ...options, behavior: "smooth" });
    }, 0);

    return new Promise((resolve) => {
      let same = 0;
      let lastPosition = 0;

      function isScrolling() {
        const newPosition = element.getBoundingClientRect().top;

        if (newPosition === lastPosition) {
          same += 1;
        } else {
          same = 0;
        }

        // Ensure a few frames are the same before considering the scrolling complete
        if (same === 10) {
          element.focus();
          resolve();
        } else {
          lastPosition = newPosition;
          requestAnimationFrame(isScrolling);
        }
      }

      requestAnimationFrame(isScrolling);
    });
  }
}
